import { Button, Divider } from '@/components'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { useAuth } from '@/contexts/auth.context'
import { Icons } from '@/theme/Images'
import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import { plaidOpen, plaidPrepare } from '@/lib/plaid-link-bus'
import { StaticAnnouncementAction } from '../../components/static-announcement'
import { useDismissFirstLoginModal } from '../hooks/use-dismiss-first-login-modal'
import { PromoCode } from '@/utils/promo-cookie'
import { useActivePromoCode } from '@/hooks/use-active-promo-code'

const getPlaidRecurringModalHeaderText = (activePromoCode) => {
  if (activePromoCode === PromoCode.NADA2026) {
    return 'Earn Extra Points on Your Car Payments as a NADA Attendee!'
  }
  return 'Earn Points on Your Lease & Loan Payments'
}

const getPlaidRecurringModalStep3Text = (activePromoCode) => {
  if (activePromoCode === PromoCode.NADA2026) {
    return (
      <>
        At the end of each month, if you&apos;ve spent $500 on your Fasten Rewards™ Visa Card, we&apos;ll calculate the points you&apos;ve earned on up to $1,000 of your lease and loan payments and apply them to your account. That&apos;s a special rate of 6x points on your loan or lease payment for the first 3 months! See{' '}
        <a
          href="https://fasten.cc/legal"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[--brand-500] font-semibold  underline-offset-4"
          aria-label="Read the Rewards Terms and Conditions"
          role="link"
        >
          Rewards Terms & Conditions
        </a>{' '}
        for more information.
      </>
    )
  }
  return (
    <>
      Earn points effortlessly. At the end of each month, if you&apos;ve spent $500 on your Fasten Rewards™ Visa Card, we&apos;ll calculate the points you&apos;ve earned on up to $1,000 of your lease and loan payments and apply them to your account. See{' '}
      <a
        href="https://fasten.cc/legal"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[--brand-500] font-semibold  underline-offset-4"
        aria-label="Read the Rewards Terms and Conditions"
        role="link"
      >
        Rewards Terms & Conditions
      </a>{' '}
      for more information.
    </>
  )
}

const PlaidRecurringModal = () => {
  const { user } = useAuth()
  const { activePromoCode } = useActivePromoCode()

  const dismissed = user.isFirstLoginModalDismissed()

  const { mutateAsync: dismissFirstLoginModalMutate } = useDismissFirstLoginModal()

  const [isFirstModalOpen, setIsFirstModalOpen] = useState(!dismissed)
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false)

  // Prepare Plaid link token as soon as the modal opens
  useEffect(() => {
    if (isFirstModalOpen) {
      plaidPrepare()
    }
  }, [isFirstModalOpen])

  const handleFirstModalOpenClose = val => {
    setIsFirstModalOpen(val)
  }

  const handleModalClosing = async () => {
    setIsFirstModalOpen(false)
    setIsSecondModalOpen(true)
    await dismissFirstLoginModalMutate()
  }

  const handleBackClick = () => {
    setIsSecondModalOpen(false)
    setIsFirstModalOpen(true)
  }

  const handleGetStarted = async () => {
    setIsFirstModalOpen(false)
    await dismissFirstLoginModalMutate()

    // Prepare and open singleton Plaid Link after dialog closes
    setTimeout(async () => {
      await plaidOpen()
    }, 100)
  }

  return (
    <>
      <Dialog onOpenChange={handleFirstModalOpenClose} open={isFirstModalOpen}>
        <DialogTrigger asChild>
          <div>
            <StaticAnnouncementAction
              id="connect-account-button"
              text="Connect account"
              handleClick={() => setIsFirstModalOpen(true)}
            />
          </div>
        </DialogTrigger>
        <DialogContent
          className="max-w-[92%] rounded-lg md:max-w-[480px] overflow-y-auto max-h-[95%]"
          data-testid="card-dialog-content"
          onInteractOutside={handleModalClosing}
          onClose={handleModalClosing}
        >
          <div className="flex flex-row justify-start items-start p-5 pb-2">
            <img src={Icons.chainIcon} alt="icon" width={48} />
            <DialogHeader className="pt-0 pl-4 w-[215px] md:w-[368px] ">
              <div className="flex flex-col justify-center items-start ">
                <DialogTitle className="leading-7 text-neutral-900 text-lg font-inter text-left">
                  {getPlaidRecurringModalHeaderText(activePromoCode)}
                </DialogTitle>
                <DialogDescription className="text-neutral-700 text-start pt-1 w-[215px] md:w-[368px]">
                  <div className="flex flex-col justify-center items-start gap-4">
                    <div>
                      <p className="font-inter text-sm font-normal leading-5 text-left">
                        We&apos;re excited to have you in the driver&apos;s seat. Ready to maximize your journey?
                        Connect your vehicle lease or loan account to get started on earning rewards and enjoying
                        exclusive perks!
                      </p>
                    </div>
                    <div>
                      <p className="font-inter text-sm font-bold leading-5 text-left">Step 1: Connect Your Account</p>
                      <p className="font-inter text-sm font-normal leading-5 text-left">
                        Connect the bank account you use for vehicle payments via Plaid. We will monitor ONLY for
                        recurring vehicle payments. Your other account activities will remain private.
                      </p>
                    </div>
                    <div>
                      <p className="font-inter text-sm font-bold leading-5 text-left">Step 2: Make Your Payments</p>
                      <p className="font-inter text-sm font-normal leading-5 text-left">
                        Make your payment as you normally would. Continue using your regular payment account for your
                        vehicle payments.
                      </p>
                    </div>
                    <div>
                      <p className="font-inter text-sm font-bold leading-5 text-left w-[215px] md:w-[370px]">
                        Step 3: Earn Points Monthly
                      </p>
                      <p className="font-inter text-sm font-normal leading-5 text-left">
                        {getPlaidRecurringModalStep3Text(activePromoCode)}
                      </p>
                    </div>
                  </div>
                </DialogDescription>
              </div>
            </DialogHeader>
          </div>
          <Divider />
          <DialogFooter className="w-full p-5">
            <Button
              id="get-started-button"
              aria-label="Get Started"
              type="button"
              className="w-full"
              onClick={handleGetStarted}
            >
              Get Started
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SkipAddingPaymentAccountModal
        isOpen={isSecondModalOpen}
        onClose={setIsSecondModalOpen}
        onBackClick={handleBackClick}
      />
    </>
  )
}

const SkipAddingPaymentAccountModal = ({ isOpen, onClose, onBackClick }) => {
  return (
    <Dialog onOpenChange={onClose} open={isOpen}>
      <DialogContent className="max-w-[92%] rounded-lg md:max-w-[480px] h-auto" data-testid="card-dialog-content">
        <div className="flex flex-row justify-start items-start p-5 pb-2">
          <img src={Icons.chainIcon} alt="icon" width={48} />
          <DialogHeader className="pt-0 pl-4 w-[215px] md:w-[368px] ">
            <div className="flex flex-col justify-center items-start ">
              <DialogTitle className="leading-7 text-neutral-900 text-lg font-inter text-left">
                Skip Adding Payment Account
              </DialogTitle>
              <DialogDescription className="text-neutral-700 text-start pt-1 w-[215px] md:w-[368px]">
                <div className="flex flex-col justify-center items-start gap-4">
                  <div>
                    <p className="font-inter text-sm font-normal leading-5 text-left">
                      Are you sure you don&apos;t want to add your account for vehicle lease or loan payments? Without
                      this, you will not be eligible to earn rewards in this category.
                    </p>
                  </div>
                </div>
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>
        <Divider />
        <DialogFooter className="w-full p-5">
          <Button
            id="skip-adding-payment-account-back-button"
            aria-label="Back"
            className="w-full text-neutral-700 border-neutral-400"
            variant="outlineSecondary"
            onClick={onBackClick}
          >
            Back
          </Button>
          <Button
            id="skip-adding-payment-account-continue-button"
            aria-label="Continue"
            className="w-full bg-error-500 hover:bg-error-700 focus:bg-error-700"
            onClick={() => onClose(false)}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

SkipAddingPaymentAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBackClick: PropTypes.func.isRequired,
}

export default PlaidRecurringModal
